<template>
  <div class="procedure-report-table-layout">
    <v-card class="admission-map-card procedure-report-card procedure-report-card--with-overlay">
      <div
        v-if="showTableLoadingOverlay"
        class="procedure-report-table-loading-overlay">
        <v-progress-circular
          indeterminate
          color="primary"
          size="40"
        />
      </div>
      <ProcedureReportPageScroll
          ref="procedureReportTable"
          class="admission-map-table procedure-report-table"
          mobile-breakpoint="0"
          enable-column-resize
          show-select
          checkbox-color="primary"
          item-key="id"
          v-model="selectedProcedureReports"
          :resizable-columns="fieldsEnableToResize"
          :headers="headers"
          :items="records"
          :loading="loadingTableAppend"
          :has-more="hasMore"
          :sort-by="sortBy ? [sortBy] : []"
          :sort-desc="sortBy ? [sortDesc] : []"
          :custom-sort="sortRecordsWithNewFirst"
          :auto-load-on-mount="false"
          :item-class="setRowClass"
          :no-data-text="$t('procedureReport.table.noData')"
          :loading-text="$t('procedureReport.messages.loadingText')"
          :no-result-text="$t('procedureReport.messages.noResultText')"
          @click.stop="(item) => onObserveRow(item)"
          @update:options="updateTableOptions"
          @load-more="loadMoreRecords">
        <template v-slot:[`item.data-table-select`]="{ isSelected, select }">
          <div class="procedure-report-select-cell">
            <v-simple-checkbox
              :value="isSelected"
              color="primary"
              @click.stop
              @input="select($event)"
            />
          </div>
        </template>
        <template v-slot:[`item.carrierId`]="{ item }">
          <div class="d-flex align-center procedure-report-carrier-cell" @click.stop="onObserveRow(item, { focusField: 'carrierId', openSelect: true })">
            <v-icon
              v-if="item.status"
              :color="getStatusColor(item.status)"
              :title="getStatusLabel(item.status)"
              size="18"
              class="procedure-report-carrier-cell__status-icon">
              {{ getStatusIcon(item.status) }}
            </v-icon>
            <ProcedureReportCarrierList
              v-if="!isRowEditing(item.id) || !item.isNew"
              class="procedure-report-readonly-field"
              :items="getCarrierItems(item.id)"
            />
            <PaginatedAutocompleteMulti
              v-else-if="isRowEditing(item.id) && item.isNew"
              :ref="inputRefName(item.id, 'carrierId')"
              :class="{
                'field-updated': isFieldUpdated(item.id, 'carrierIds')
              }"
              v-bind="baseMultiInputProps"
              :fetch="fetchEditingCarriers"
              :prepend-items="() => getEditingOptions(item.id, 'carrierIds')"
              :group-by-header="false"
              item-text="name"
              item-value="id"
              :value="getRawFieldValue(item.id, 'carrierIds')"
              @click.stop
              @input="onFieldChange(item.id, 'carrierIds', $event)"
              @keydown.tab.exact.prevent="focusNextField(item.id, 'carrierId')"
              @keydown.shift.tab.prevent="focusPreviousField(item.id, 'carrierId')"
            />
          </div>
        </template>
        <template v-slot:[`item.procedureCodeId`]="{ item }">
          <OneLineCell
            v-if="!isRowEditing(item.id)"
            class="procedure-report-readonly-field"
            wrapper-class="procedure-report-readonly-field__content"
            :text="getFieldDisplayValue(item.id, 'procedureCodeId')"
            @click.native.stop="onObserveRow(item, { focusField: 'procedureCodeId' })"
          />
          <Textfield
            v-else
            :ref="inputRefName(item.id, 'procedureCodeId')"
            :class="{
              'field-updated': isFieldUpdated(item.id, 'procedureCodeId')
            }"
            :textColor="rowError(item) ? 'foreground-accent' : ''"
            dense
            hideDetails
            outlined
            :value="getRawFieldValue(item.id, 'procedureCodeId')"
            @click.stop
            @input="onFieldChange(item.id, 'procedureCodeId', $event)"
            @keydown.tab.exact.prevent="focusNextField(item.id, 'procedureCodeId')"
            @keydown.shift.tab.prevent="focusPreviousField(item.id, 'procedureCodeId')"
          />
        </template>
        <template v-slot:[`item.procedureDescriptionId`]="{ item }">
          <OneLineCell
            v-if="!isRowEditing(item.id)"
            class="procedure-report-readonly-field"
            wrapper-class="procedure-report-readonly-field__content"
            :text="getFieldDisplayValue(item.id, 'procedureDescriptionId')"
            @click.native.stop="onObserveRow(item, { focusField: 'procedureDescriptionId' })"
          />
          <Textfield
            v-else
            :ref="inputRefName(item.id, 'procedureDescriptionId')"
            :class="{
              'field-updated': isFieldUpdated(item.id, 'procedureDescriptionId')
            }"
            :textColor="rowError(item) ? 'foreground-accent' : ''"
            dense
            hideDetails
            outlined
            readonly
            :value="getFieldDisplayValue(item.id, 'procedureDescriptionId')"
            @focus="onDialogFieldFocus(item, 'procedureDescriptionId')"
            @click.stop="onDialogFieldClick(item, 'procedureDescriptionId')"
            @keydown.tab.exact.prevent="focusNextField(item.id, 'procedureDescriptionId')"
            @keydown.shift.tab.prevent="focusPreviousField(item.id, 'procedureDescriptionId')"
          />
        </template>
        <template v-slot:[`item.tussCodeId`]="{ item }">
          <OneLineCell
            v-if="!isRowEditing(item.id)"
            class="procedure-report-readonly-field"
            wrapper-class="procedure-report-readonly-field__content"
            :text="getFieldDisplayValue(item.id, 'tussCodeId')"
            @click.native.stop="onObserveRow(item, { focusField: 'tussCodeId' })"
          />
          <Textfield
            v-else
            :ref="inputRefName(item.id, 'tussCodeId')"
            :class="{
              'field-updated': isFieldUpdated(item.id, 'tussCodeId')
            }"
            :textColor="rowError(item) ? 'foreground-accent' : ''"
            dense
            hideDetails
            outlined
            :value="getRawFieldValue(item.id, 'tussCodeId')"
            @click.stop
            @input="onFieldChange(item.id, 'tussCodeId', $event)"
            @keydown.tab.exact.prevent="focusNextField(item.id, 'tussCodeId')"
            @keydown.shift.tab.prevent="focusPreviousField(item.id, 'tussCodeId')"
          />
        </template>
        <template v-slot:[`item.eventId`]="{ item }">
          <OneLineCell
            v-if="!isRowEditing(item.id)"
            class="procedure-report-readonly-field"
            wrapper-class="procedure-report-readonly-field__content"
            :text="getFieldDisplayValue(item.id, 'eventId')"
            @click.native.stop="onObserveRow(item, { focusField: 'eventId', openSelect: true })"
          />
          <PaginatedAutocomplete
            v-else
            :key="dimensionInputKey(item.id, 'eventId')"
            :ref="inputRefName(item.id, 'eventId')"
            :class="{
              'ui-field--disabled': !isRowEditing(item.id),
              'field-updated': isFieldUpdated(item.id, 'eventId'),
              'procedure-report-field--loading': isDimensionFieldResolving(item.id, 'eventId')
            }"
            v-bind="baseInputProps"
            :allow-create="true"
            :fetch="fetchEditingEvents"
            :prepend-items="() => getEditingOptions(item.id, 'eventId')"
            item-text="name"
            item-value="id"
            :disabled="isDimensionFieldResolving(item.id, 'eventId')"
            :hide-icon="!isRowEditing(item.id)"
            :solo="!isRowEditing(item.id)"
            :flat="!isRowEditing(item.id)"
            :readonly="!isRowEditing(item.id)"
            :value="getRawFieldValue(item.id, 'eventId')"
            @click.stop="onObserveRow(item, { focusField: 'eventId', openSelect: true })"
            @input="onFieldChange(item.id, 'eventId', $event)"
            @keydown.tab.exact.prevent="focusNextField(item.id, 'eventId')"
            @keydown.shift.tab.prevent="focusPreviousField(item.id, 'eventId')"
          />
        </template>
        <template v-slot:[`item.groupId`]="{ item }">
          <OneLineCell
            v-if="!isRowEditing(item.id)"
            class="procedure-report-readonly-field"
            wrapper-class="procedure-report-readonly-field__content"
            :text="getFieldDisplayValue(item.id, 'groupId')"
            @click.native.stop="onObserveRow(item, { focusField: 'groupId', openSelect: true })"
          />
          <PaginatedAutocomplete
            v-else
            :key="dimensionInputKey(item.id, 'groupId')"
            :ref="inputRefName(item.id, 'groupId')"
            :class="{
              'ui-field--disabled': !isRowEditing(item.id),
              'field-updated': isFieldUpdated(item.id, 'groupId'),
              'procedure-report-field--loading': isDimensionFieldResolving(item.id, 'groupId')
            }"
            v-bind="baseInputProps"
            :allow-create="true"
            :fetch="fetchEditingGroups"
            :prepend-items="() => getEditingOptions(item.id, 'groupId')"
            item-text="name"
            item-value="id"
            :disabled="isDimensionFieldResolving(item.id, 'groupId')"
            :hide-icon="!isRowEditing(item.id)"
            :solo="!isRowEditing(item.id)"
            :flat="!isRowEditing(item.id)"
            :readonly="!isRowEditing(item.id)"
            :value="getRawFieldValue(item.id, 'groupId')"
            @click.stop="onObserveRow(item, { focusField: 'groupId', openSelect: true })"
            @input="onFieldChange(item.id, 'groupId', $event)"
            @keydown.tab.exact.prevent="focusNextField(item.id, 'groupId')"
            @keydown.shift.tab.prevent="focusPreviousField(item.id, 'groupId')"
          />
        </template>
        <template v-slot:[`item.subGroupId`]="{ item }">
          <OneLineCell
            v-if="!isRowEditing(item.id)"
            class="procedure-report-readonly-field"
            wrapper-class="procedure-report-readonly-field__content"
            :text="getFieldDisplayValue(item.id, 'subGroupId')"
            @click.native.stop="onObserveRow(item, { focusField: 'subGroupId', openSelect: true })"
          />
          <PaginatedAutocomplete
            v-else
            :key="dependentInputKey(item.id, 'subGroupId', 'groupId')"
            :ref="inputRefName(item.id, 'subGroupId')"
            :class="{
              'ui-field--disabled': !isRowEditing(item.id),
              'field-updated': isFieldUpdated(item.id, 'subGroupId'),
              'procedure-report-field--loading': isDimensionFieldResolving(item.id, 'subGroupId')
            }"
            v-bind="baseInputProps"
            :allow-create="true"
            :fetch="(page, size, inputValue) => fetchEditingSubGroups(item.id, page, size, inputValue)"
            :prepend-items="() => getDependentEditingOptions(item.id, 'subGroupId')"
            :disabled="!hasExistingDimensionValue(item.id, 'groupId') || isDimensionFieldResolving(item.id, 'subGroupId')"
            item-text="name"
            item-value="id"
            :hide-icon="!isRowEditing(item.id)"
            :solo="!isRowEditing(item.id)"
            :flat="!isRowEditing(item.id)"
            :readonly="!isRowEditing(item.id)"
            :value="getRawFieldValue(item.id, 'subGroupId')"
            @click.stop="onObserveRow(item, { focusField: 'subGroupId', openSelect: true })"
            @input="onFieldChange(item.id, 'subGroupId', $event)"
            @keydown.tab.exact.prevent="focusNextField(item.id, 'subGroupId')"
            @keydown.shift.tab.prevent="focusPreviousField(item.id, 'subGroupId')"
          />
        </template>
        <template v-slot:[`item.complementId`]="{ item }">
          <OneLineCell
            v-if="!isRowEditing(item.id)"
            class="procedure-report-readonly-field"
            wrapper-class="procedure-report-readonly-field__content"
            :text="getFieldDisplayValue(item.id, 'complementId')"
            @click.native.stop="onObserveRow(item, { focusField: 'complementId', openSelect: true })"
          />
          <PaginatedAutocomplete
            v-else
            :key="dependentInputKey(item.id, 'complementId', 'eventId')"
            :ref="inputRefName(item.id, 'complementId')"
            :class="{
              'ui-field--disabled': !isRowEditing(item.id),
              'field-updated': isFieldUpdated(item.id, 'complementId'),
              'procedure-report-field--loading': isDimensionFieldResolving(item.id, 'complementId')
            }"
            v-bind="baseInputProps"
            :allow-create="true"
            :fetch="(page, size, inputValue) => fetchEditingComplements(item.id, page, size, inputValue)"
            :prepend-items="() => getDependentEditingOptions(item.id, 'complementId')"
            :disabled="!hasExistingDimensionValue(item.id, 'eventId') || isDimensionFieldResolving(item.id, 'complementId')"
            item-text="name"
            item-value="id"
            :hide-icon="!isRowEditing(item.id)"
            :solo="!isRowEditing(item.id)"
            :flat="!isRowEditing(item.id)"
            :readonly="!isRowEditing(item.id)"
            :value="getRawFieldValue(item.id, 'complementId')"
            @click.stop="onObserveRow(item, { focusField: 'complementId', openSelect: true })"
            @input="onFieldChange(item.id, 'complementId', $event)"
            @keydown.tab.exact.prevent="focusNextField(item.id, 'complementId')"
            @keydown.shift.tab.prevent="focusPreviousField(item.id, 'complementId')"
          />
        </template>
        <template v-slot:[`item.predictiveId`]="{ item }">
          <OneLineCell
            v-if="!isRowEditing(item.id)"
            class="procedure-report-readonly-field"
            wrapper-class="procedure-report-readonly-field__content"
            :text="getFieldDisplayValue(item.id, 'predictiveId')"
            @click.native.stop="onObserveRow(item, { focusField: 'predictiveId', openSelect: true })"
          />
          <PaginatedAutocomplete
            v-else
            :key="dimensionInputKey(item.id, 'predictiveId')"
            :ref="inputRefName(item.id, 'predictiveId')"
            :class="{
              'ui-field--disabled': !isRowEditing(item.id),
              'field-updated': isFieldUpdated(item.id, 'predictiveId'),
              'procedure-report-field--loading': isDimensionFieldResolving(item.id, 'predictiveId')
            }"
            v-bind="baseInputProps"
            :allow-create="true"
            :fetch="fetchEditingPredictives"
            :prepend-items="() => getEditingOptions(item.id, 'predictiveId')"
            item-text="name"
            item-value="id"
            :disabled="isDimensionFieldResolving(item.id, 'predictiveId')"
            :hide-icon="!isRowEditing(item.id)"
            :solo="!isRowEditing(item.id)"
            :flat="!isRowEditing(item.id)"
            :readonly="!isRowEditing(item.id)"
            :value="getRawFieldValue(item.id, 'predictiveId')"
            @click.stop="onObserveRow(item, { focusField: 'predictiveId', openSelect: true })"
            @input="onFieldChange(item.id, 'predictiveId', $event)"
            @keydown.tab.exact.prevent="focusNextField(item.id, 'predictiveId')"
            @keydown.shift.tab.prevent="focusPreviousField(item.id, 'predictiveId')"
          />
        </template>
        <template v-slot:[`item.predictiveClassificationId`]="{ item }">
          <OneLineCell
            v-if="!isRowEditing(item.id)"
            class="procedure-report-readonly-field"
            wrapper-class="procedure-report-readonly-field__content"
            :text="getFieldDisplayValue(item.id, 'predictiveClassificationId')"
            @click.native.stop="onObserveRow(item, { focusField: 'predictiveClassificationId', openSelect: true })"
          />
          <PaginatedAutocomplete
            v-else
            :key="dependentInputKey(item.id, 'predictiveClassificationId', 'predictiveId')"
            :ref="inputRefName(item.id, 'predictiveClassificationId')"
            :class="{
              'ui-field--disabled': !isRowEditing(item.id),
              'field-updated': isFieldUpdated(item.id, 'predictiveClassificationId'),
              'procedure-report-field--loading': isDimensionFieldResolving(item.id, 'predictiveClassificationId')
            }"
            v-bind="baseInputProps"
            :allow-create="true"
            :fetch="(page, size, inputValue) => fetchEditingPredictiveClassifications(item.id, page, size, inputValue)"
            :prepend-items="() => getDependentEditingOptions(item.id, 'predictiveClassificationId')"
            :disabled="!hasExistingDimensionValue(item.id, 'predictiveId') || isDimensionFieldResolving(item.id, 'predictiveClassificationId')"
            item-text="name"
            item-value="id"
            :hide-icon="!isRowEditing(item.id)"
            :solo="!isRowEditing(item.id)"
            :flat="!isRowEditing(item.id)"
            :readonly="!isRowEditing(item.id)"
            :value="getRawFieldValue(item.id, 'predictiveClassificationId')"
            @click.stop="onObserveRow(item, { focusField: 'predictiveClassificationId', openSelect: true })"
            @input="onFieldChange(item.id, 'predictiveClassificationId', $event)"
            @keydown.tab.exact.prevent="focusNextField(item.id, 'predictiveClassificationId')"
            @keydown.shift.tab.prevent="focusPreviousField(item.id, 'predictiveClassificationId')"
          />
        </template>
        <template v-slot:[`item.actions`]="{ item }">
          <div class="d-flex flex-row justify-center align-center">
            <v-icon
              v-if="rowSuccess(item)"
              color="primary"
              size="18">
              fas fa-check
            </v-icon>

            <v-tooltip
              bottom
              v-if="item.isNew || hasRowUpdates(item.id) || rowError(item)">
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  :loading="isRowSaving(item.id)"
                  v-on="on"
                  v-bind="attrs"
                  @click.stop="trySaveAgain(item)"
                  solo
                  icon>
                  <v-img
                    contain
                    height="18"
                    :src="rowError(item) ? 'icons/map-save-white.svg' : 'icons/map-save.svg'"
                  />
                </v-btn>
              </template>
              <span>
                {{ $t('common.save') }}
              </span>
            </v-tooltip>

            <v-menu
              v-if="rowError(item)"
              bottom
              offset-y
              open-on-hover
              :close-delay="150"
              :open-delay="100"
              content-class="procedure-report-row-error-menu">
              <template v-slot:activator="{ on, attrs }">
                <div
                  class="d-inline-flex"
                  v-on="on"
                  v-bind="attrs">
                  <v-img
                    contain
                    height="18"
                    src="icons/alert-triangle.svg"
                  />
                </div>
              </template>

              <v-card class="procedure-report-row-error-card" outlined>
                <div
                  v-if="hasRowErrorItems(item.id)"
                  class="procedure-report-row-error-card__body">
                  <ul class="procedure-report-row-error-card__list">
                    <li
                      v-for="(message, index) in getRowErrorItems(item.id)"
                      :key="`${item.id}-error-${index}`">
                      {{ message }}
                    </li>
                  </ul>
                </div>
                <div v-else class="procedure-report-row-error-card__body">
                  {{ getRowErrorMessage(item.id) }}
                </div>
              </v-card>
            </v-menu>

            <v-tooltip
              v-if="item.isNew"
              bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-on="on"
                  v-bind="attrs"
                  :disabled="isRowSaving(item.id)"
                  @click.stop="discardNewProcedure(item.id)"
                  solo
                  icon>
                  <v-icon
                    size="22"
                    :color="rowError(item) ? 'foreground-accent' : 'error'">
                    mdi-delete-outline
                  </v-icon>
                </v-btn>
              </template>
              <span>
                {{ $t('procedureReport.actions.rowActions.discardNewRegister') }}
              </span>
            </v-tooltip>

            <ProcedureReportRowActionsMenu
              v-if="shouldShowRowActionsMenu(item)"
              :item="item"
              :disabled="isRowSaving(item.id)"
              @approve="approveProcedure"
              @justification="openJustification"
              @history="showRecordHistory(item.id)"
            />
          </div>
        </template>
        <template v-slot:no-data>
          <div class="py-6 text-center admission-map-text-base grey--text">
            {{ $t('procedureReport.table.noData') }}
          </div>
        </template>
      </ProcedureReportPageScroll>
    </v-card>

    <AdmissionMapFieldDialog
      :key="`${fieldDialog.rowId || 'no-row'}-${fieldDialog.field || 'no-field'}`"
      :visible="fieldDialog.visible"
      :value="fieldDialog.value"
      :title="fieldDialogTitle"
      @cancel="cancelFieldDialog"
      @save="saveFieldDialog"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex';

import ProcedureReportPageScroll from '@/components/ProcedureReport/ProcedureReportPageScroll.vue';
import ProcedureReportCarrierList from '@/components/ProcedureReport/ProcedureReportCarrierList.vue';
import OneLineCell from '@/components/Default/OneLineCell.vue';
import PaginatedAutocomplete from '@/components/Default/PaginatedAutocomplete.vue';
import PaginatedAutocompleteMulti from '@/components/Default/PaginatedAutocompleteMulti.vue';
import Textfield from '@/components/Default/Textfield.vue';
import AdmissionMapFieldDialog from '@/components/AdmissionMap/AdmissionMapFieldDialog.vue';
import ProcedureReportRowActionsMenu from '@/components/ProcedureReport/ProcedureReportRowActionsMenu.vue';
import { translateBackendErrorMessage } from '@/store/modules/procedure-report/procedure-report-error-translator';
import {
  getDefaultStatusConfig,
  getStatusConfig,
} from '@/store/modules/procedure-report/procedure-report-config';

export default {
  name: 'ProcedureReportTable',
  components: {
    ProcedureReportPageScroll,
    ProcedureReportCarrierList,
    OneLineCell,
    PaginatedAutocomplete,
    PaginatedAutocompleteMulti,
    Textfield,
    AdmissionMapFieldDialog,
    ProcedureReportRowActionsMenu,
  },
  data: () => ({
    formRows: {},
    updatedFields: {},
    editingRows: {},
    selectedProcedureReports: [],
    editableFields: [
      'carrierId',
      'procedureCodeId',
      'procedureDescriptionId',
      'tussCodeId',
      'eventId',
      'complementId',
      'groupId',
      'subGroupId',
      'predictiveId',
      'predictiveClassificationId',
    ],
    dialogEditableFields: [
      'procedureDescriptionId',
    ],
    requiredNewRecordFields: [
      'carrierIds',
      'procedureCodeId',
    ],
    currentlyFocusedField: null,
    recordIdHistoryInLoading: null,
    rowIdsUpdated: [],
    savingRows: {},
    rowErrors: {},
    editingOptionRequests: {},
    dimensionResolutionRequests: {},
    rowIdWithError: [],
    fieldDialog: {
      visible: false,
      rowId: null,
      field: null,
      value: '',
    },
    skipDialogFieldFocus: {},
    fieldsEnableToResize: [
      'carrierId',
      'procedureCodeId',
      'procedureDescriptionId',
      'tussCodeId',
      'eventId',
      'complementId',
      'groupId',
      'subGroupId',
      'predictiveId',
      'predictiveClassificationId',
    ],
  }),
  computed: {
    ...mapGetters({
      predictiveClassificationOptions: 'procedure-report/predictiveClassificationOptions',
      procedureDescriptionOptions: 'procedure-report/procedureDescriptionOptions',
      procedureCodeOptions: 'procedure-report/procedureCodeOptions',
      predictiveOptions: 'procedure-report/predictiveOptions',
      complementOptions: 'procedure-report/complementOptions',
      tussCodeOptions: 'procedure-report/tussCodeOptions',
      subGroupOptions: 'procedure-report/subGroupOptions',
      carrierOptions: 'procedure-report/carrierOptions',
      loadingRecordHistories: 'procedure-report/loadingRecordHistories',
      loadingTable: 'procedure-report/loadingTable',
      loadingTableAppend: 'procedure-report/loadingTableAppend',
      itemsPerPage: 'procedure-report/itemsPerPage',
      groupOptions: 'procedure-report/groupOptions',
      eventOptions: 'procedure-report/eventOptions',
      currentPage: 'procedure-report/currentPage',
      totalItems: 'procedure-report/totalItems',
      clickAway: 'procedure-report/clickAway',
      sortDesc: 'procedure-report/sortDesc',
      records: 'procedure-report/records',
      sortBy: 'procedure-report/sortBy',
      procedureReportsSelecteds: 'procedure-report/procedureReportsSelecteds',
      filter: 'procedure-report/filter',
      hasFiltersSelected: 'procedure-report/hasFiltersSelected',
    }),
    filteredCarrierOptions() {
      const carrierIds = new Set((this.filter.carrierId || []).map(String));
      if (carrierIds.size === 0) return this.carrierOptions;
      return this.carrierOptions.filter((option) => carrierIds.has(String(option.id)));
    },
    headers() {
      return [
        {
          text: '', value: 'data-table-select', sortable: false, width: 40, class: 'col-select', cellClass: 'col-select',
        },
        {
          text: this.$t('procedureReport.table.headers.carrier'), value: 'carrierId', sortable: true, width: 150, class: 'col-procedure-carrier', cellClass: 'col-procedure-carrier',
        },
        {
          text: this.$t('procedureReport.table.headers.procedureCode'), value: 'procedureCodeId', sortable: true, width: 185, class: 'col-procedure-code', cellClass: 'col-procedure-code',
        },
        {
          text: this.$t('procedureReport.table.headers.procedureDescription'), value: 'procedureDescriptionId', sortable: true, width: 260, class: 'col-procedure-description', cellClass: 'col-procedure-description',
        },
        {
          text: this.$t('procedureReport.table.headers.tussCode'), value: 'tussCodeId', sortable: true, width: 145, class: 'col-procedure-tuss', cellClass: 'col-procedure-tuss',
        },
        {
          text: this.$t('procedureReport.table.headers.event'), value: 'eventId', sortable: true, width: 120, class: 'col-procedure-event', cellClass: 'col-procedure-event',
        },
        {
          text: this.$t('procedureReport.table.headers.complement'), value: 'complementId', sortable: true, width: 180, class: 'col-procedure-complement', cellClass: 'col-procedure-complement',
        },
        {
          text: this.$t('procedureReport.table.headers.group'), value: 'groupId', sortable: true, width: 115, class: 'col-procedure-group', cellClass: 'col-procedure-group',
        },
        {
          text: this.$t('procedureReport.table.headers.subGroup'), value: 'subGroupId', sortable: true, width: 145, class: 'col-procedure-subgroup', cellClass: 'col-procedure-subgroup',
        },
        {
          text: this.$t('procedureReport.table.headers.predictive'), value: 'predictiveId', sortable: true, width: 135, class: 'col-procedure-predictive', cellClass: 'col-procedure-predictive',
        },
        {
          text: this.$t('procedureReport.table.headers.predictiveClassification'), value: 'predictiveClassificationId', sortable: true, width: 235, class: 'col-procedure-predictive-classification', cellClass: 'col-procedure-predictive-classification',
        },
        {
          text: this.$t('common.actions'), value: 'actions', sortable: false, width: 100, align: 'center', class: 'col-procedure-action text-center', cellClass: 'col-procedure-action text-center',
        },
      ];
    },
    baseInputProps() {
      return {
        clearable: true,
        dense: true,
        hideDetails: true,
        minSearchChars: 0,
        openMenuAfterLoad: true,
      };
    },
    baseMultiInputProps() {
      return {
        clearable: true,
        dense: true,
        hideDetails: true,
        minSearchChars: 0,
      };
    },
    hasMore() {
      if (!this.totalItems) return false;
      return this.records.length < this.totalItems;
    },
    showTableLoadingOverlay() {
      return this.loadingTable && !this.loadingTableAppend;
    },
    activeNewRecord() {
      return this.records.find((record) => record.isNew) || null;
    },
    fieldDialogTitle() {
      const titles = {
        procedureCodeId: this.$t('procedureReport.modals.editProcedureCode'),
        procedureDescriptionId: this.$t('procedureReport.modals.editProcedureDescription'),
        tussCodeId: this.$t('procedureReport.modals.editTussCode'),
      };
      return titles[this.fieldDialog.field] || '';
    },
  },
  watch: {
    records: {
      handler(records) {
        this.syncFormRows(records);
      },
      immediate: true,
    },
    selectedProcedureReports: {
      handler(procedureReportsSelecteds) {
        this.setProcedureReportsSelected(procedureReportsSelecteds);
      },
      deep: true,
    },
    procedureReportsSelecteds(procedureReportsSelecteds) {
      if (procedureReportsSelecteds.length === 0 && this.selectedProcedureReports.length > 0) {
        this.selectedProcedureReports = [];
      }
    },
    clickAway: {
      handler(clicked) {
        if (clicked) {
          this.onObserveRow(null);
        }
      },
    },
  },
  methods: {
    ...mapActions({
      updateTableOptions: 'procedure-report/updateTableOptions',
      saveProcedureRecord: 'procedure-report/saveRecord',
      createProcedureDimension: 'procedure-report/createRecordDimension',
      addProcedureRecord: 'procedure-report/addNewRecord',
      discardProcedureRecord: 'procedure-report/discardNewRecord',
      fetchProcedureHistory: 'procedure-report/fetchProcedureHistory',
      fetchCarrierOptions: 'procedure-report/fetchCarrierOptions',
      fetchEventOptions: 'procedure-report/fetchEventOptions',
      fetchGroupOptions: 'procedure-report/fetchGroupOptions',
      fetchSubGroupOptions: 'procedure-report/fetchSubGroupOptions',
      fetchComplementOptions: 'procedure-report/fetchComplementOptions',
      fetchPredictiveOptions: 'procedure-report/fetchPredictiveOptions',
      fetchPredictiveClassificationOptions: 'procedure-report/fetchPredictiveClassificationOptions',
      fetchClassification: 'procedure-report/fetchClassification',
      approveProcedures: 'procedure-report/approveProcedures',
      openJustificationModal: 'procedure-report/openJustificationModal',
    }),
    ...mapMutations({ setProceduresSelected: 'procedure-report/SET_PROCEDURE_REPORTS_SELECTEDS', showProcedureReportSnackbar: 'procedure-report/showSnackbar' }),
    setProcedureReportsSelected(procedureReportsSelecteds) {
      this.setProceduresSelected(procedureReportsSelecteds);
    },
    sortRecordsWithNewFirst(records) {
      const newRecords = records.filter((record) => record.isNew);
      const existingRecords = records.filter((record) => !record.isNew);
      return [...newRecords, ...existingRecords];
    },
    paginateLocalOptions(options, page, size, inputValue) {
      const search = String(inputValue || '').toLowerCase();
      const filtered = search
        ? options.filter((option) => option.name.toLowerCase().includes(search))
        : options;
      const sorted = [...filtered].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      const start = page * size;
      const content = sorted.slice(start, start + size);
      return {
        content,
        last: start + size >= sorted.length,
        totalElements: sorted.length,
      };
    },
    async fetchEditingCarriers(page, size, inputValue) {
      return this.paginateLocalOptions(this.filteredCarrierOptions, page, size, inputValue);
    },
    async fetchEditingEvents(page, size, inputValue) {
      return this.fetchSharedEditingOptions('eventId', this.fetchEventOptions, page, size, inputValue);
    },
    async fetchEditingGroups(page, size, inputValue) {
      return this.fetchSharedEditingOptions('groupId', this.fetchGroupOptions, page, size, inputValue);
    },
    async fetchEditingSubGroups(rowId, page, size, inputValue) {
      return this.fetchSharedEditingOptions(
        'subGroupId',
        this.fetchSubGroupOptions,
        page,
        size,
        inputValue,
        { groupIds: this.getExistingDimensionIds(rowId, 'groupId') },
      );
    },
    async fetchEditingComplements(rowId, page, size, inputValue) {
      return this.fetchSharedEditingOptions(
        'complementId',
        this.fetchComplementOptions,
        page,
        size,
        inputValue,
        { eventIds: this.getExistingDimensionIds(rowId, 'eventId') },
      );
    },
    async fetchEditingPredictives(page, size, inputValue) {
      return this.fetchSharedEditingOptions('predictiveId', this.fetchPredictiveOptions, page, size, inputValue);
    },
    async fetchEditingPredictiveClassifications(rowId, page, size, inputValue) {
      return this.fetchSharedEditingOptions(
        'predictiveClassificationId',
        this.fetchPredictiveClassificationOptions,
        page,
        size,
        inputValue,
        { predictiveIds: this.getExistingDimensionIds(rowId, 'predictiveId') },
      );
    },
    getExistingDimensionIds(rowId, field) {
      const value = this.getRawFieldValue(rowId, field);
      if (value === null || value === undefined || value === '') return [];
      if (typeof value === 'number') return [value];

      const option = this.getFieldOptions(field)
        .find((item) => String(item.id) === String(value));
      return option ? [option.id] : [];
    },
    fetchSharedEditingOptions(field, fetchOptions, page, size, inputValue, requestParams = {}) {
      const search = String(inputValue || '').trim().toLowerCase();
      const requestKey = `${field}:${search}:${page}:${size}:${JSON.stringify(requestParams)}`;
      const cachedRequest = this.editingOptionRequests[requestKey];

      if (cachedRequest) return cachedRequest;

      const payload = { page, size, inputValue, ...requestParams };
      const request = Promise.resolve()
        .then(() => fetchOptions.call(this, payload))
        .catch((error) => {
          this.$delete(this.editingOptionRequests, requestKey);
          throw error;
        });

      this.$set(this.editingOptionRequests, requestKey, request);
      return request;
    },
    rowSuccess(item) {
      const isSuccess = this.setRowClass(item) && this.setRowClass(item).includes('success');
      return !!isSuccess;
    },
    rowError(item) {
      const isError = this.setRowClass(item) && this.setRowClass(item).includes('error');
      return !!isError;
    },
    isRowSaving(rowId) {
      return Boolean(this.savingRows[rowId]);
    },
    getRowError(rowId) {
      return this.rowErrors[rowId] || null;
    },
    getRowErrorMessage(rowId) {
      return this.getRowError(rowId)?.message || this.$t('procedureReport.messages.saveError');
    },
    getRowErrorItems(rowId) {
      return this.getRowError(rowId)?.items || [];
    },
    hasRowErrorItems(rowId) {
      return this.getRowErrorItems(rowId).length > 0;
    },
    setRowErrorInfo(rowId, errorInfo) {
      this.$set(this.rowErrors, rowId, errorInfo);
    },
    clearRowErrorInfo(rowId) {
      if (this.rowErrors[rowId]) this.$delete(this.rowErrors, rowId);
    },
    getRequestErrorResponse(result) {
      if (!result || typeof result !== 'object') return null;
      return result.response || null;
    },
    isValidationEntityError(errorData) {
      return Boolean(
        errorData
        && Number(errorData.code) === 422
        && Array.isArray(errorData.fields),
      );
    },
    translateValidationFieldMessage(field, fallbackMessage) {
      const translationKey = `procedureReport.messages.validationFields.${field}`;
      const translated = String(this.$t(translationKey));

      if (translated && translated !== translationKey) return translated;
      return fallbackMessage || String(this.$t('procedureReport.messages.saveError'));
    },
    buildValidationFieldErrorItems(errorData) {
      if (!this.isValidationEntityError(errorData)) return [];

      const fieldAliases = {
        procedureCode: 'procedureCodeId',
        procedureDescription: 'procedureDescriptionId',
        tussCode: 'tussCodeId',
        subgroupId: 'subGroupId',
      };
      const tableFieldOrder = new Map(
        this.headers.map((header, index) => [header.value, index]),
      );
      const uniqueMessages = new Set();

      errorData.fields
        .map((item, originalIndex) => ({
          item,
          originalIndex,
          tableField: fieldAliases[item?.field] || item?.field,
        }))
        .filter(({ item }) => Boolean(item))
        .sort((a, b) => {
          const aOrder = tableFieldOrder.has(a.tableField)
            ? tableFieldOrder.get(a.tableField)
            : Number.MAX_SAFE_INTEGER;
          const bOrder = tableFieldOrder.has(b.tableField)
            ? tableFieldOrder.get(b.tableField)
            : Number.MAX_SAFE_INTEGER;
          return aOrder !== bOrder ? aOrder - bOrder : a.originalIndex - b.originalIndex;
        })
        .forEach(({ item, tableField }) => {
          const message = this.translateValidationFieldMessage(tableField, item.message);
          if (message) uniqueMessages.add(message);
        });

      return Array.from(uniqueMessages);
    },
    buildRowErrorInfo(result) {
      const response = this.getRequestErrorResponse(result);
      const data = response?.data;
      const status = response?.status ?? result;

      if (status === 403) {
        return {
          message: String(this.$t('procedureReport.messages.noPermission')),
          items: [],
        };
      }

      const items = this.buildValidationFieldErrorItems(data);
      if (items.length > 0) {
        return {
          message: String(this.$t('procedureReport.messages.saveError')),
          items,
        };
      }

      const translatedMessage = translateBackendErrorMessage(data?.detail || data?.message);
      return {
        message: String(translatedMessage || result?.message || this.$t('procedureReport.messages.saveError')),
        items,
      };
    },
    setRowRequestError(rowId, result) {
      this.addRowIdWithError(rowId);
      this.setRowErrorInfo(rowId, this.buildRowErrorInfo(result));
    },
    hasFieldValue(value) {
      if (Array.isArray(value)) return value.length > 0;
      if (value === null || value === undefined) return false;
      return typeof value !== 'string' || Boolean(value.trim());
    },
    getMissingRequiredFields(rowId) {
      return this.requiredNewRecordFields.filter(
        (field) => !this.hasFieldValue(this.getRawFieldValue(rowId, field)),
      );
    },
    validateNewRecord(rowId) {
      if (!this.findRecord(rowId)?.isNew) return true;

      const missingFields = this.getMissingRequiredFields(rowId);
      if (!missingFields.length) return true;

      this.addRowIdWithError(rowId);
      this.setRowErrorInfo(rowId, {
        message: String(this.$t('procedureReport.messages.saveError')),
        items: missingFields.map((field) => this.translateValidationFieldMessage(field)),
      });
      return false;
    },
    async trySaveAgain(item) {
      this.commitRowFieldValues(item.id);
      await this.$nextTick();
      await this.saveRecord(item.id);
    },
    async showRecordHistory(rowId) {
      this.recordIdHistoryInLoading = rowId;
      await this.fetchProcedureHistory(rowId);
      this.recordIdHistoryInLoading = null;
    },
    clearUpdatedFields(rowId) {
      if (this.updatedFields[rowId]) {
        this.$set(this.updatedFields, rowId, {});
      }
    },
    disableEditing(rowId) {
      if (this.editingRows[rowId]) {
        this.$set(this.editingRows, rowId, false);
      }
    },
    enableEditing(rowId, focusField = 'carrierId') {
      this.ensureFormRow(rowId);
      this.$set(this.editingRows, rowId, true);
      if (focusField) {
        this.$nextTick(() => {
          this.focusField(rowId, focusField);
        });
      }
      return true;
    },
    ensureFormRow(rowId) {
      if (!this.formRows[rowId]) {
        const record = this.findRecord(rowId);
        this.$set(this.formRows, rowId, record ? { ...record } : { id: rowId });
      }
    },
    findRecord(rowId) {
      return this.records.find((record) => record.id === rowId);
    },
    releaseRowEditing(rowId) {
      this.disableEditing(rowId);
      if (String(this.currentlyFocusedField) === String(rowId)) {
        this.currentlyFocusedField = null;
      }
    },
    async finalizeRowEdit(rowId) {
      if (!rowId || !this.isRowEditing(rowId)) {
        if (String(this.currentlyFocusedField) === String(rowId)) {
          this.currentlyFocusedField = null;
        }
        return true;
      }

      if (this.findRecord(rowId)?.isNew) return false;

      if (this.rowHasChanges(rowId)) {
        return this.saveRecord(rowId);
      }

      this.clearUpdatedFields(rowId);
      this.releaseRowEditing(rowId);
      return true;
    },
    getEditableFieldsForRow(rowId) {
      const isNew = this.findRecord(rowId)?.isNew;
      const base = isNew
        ? this.editableFields
        : this.editableFields.filter(
          (field) => field !== 'carrierId',
        );
      return base;
    },
    async focusPreviousField(rowId, field) {
      if (!this.isRowEditing(rowId)) return;
      const fieldCommitted = await this.commitAndCreateFieldValue(rowId, field);
      if (!fieldCommitted) return;

      const fields = this.getEditableFieldsForRow(rowId);
      const fieldIndex = fields.indexOf(field);
      const rowIndex = this.records.findIndex((record) => String(record.id) === String(rowId));
      if (fieldIndex === -1 || rowIndex === -1) return;

      let targetRowIndex = rowIndex;
      let targetFieldIndex = fieldIndex - 1;

      if (targetFieldIndex < 0) {
        targetRowIndex = rowIndex - 1;
        if (targetRowIndex >= 0) {
          const targetFields = this.getEditableFieldsForRow(this.records[targetRowIndex]?.id);
          targetFieldIndex = targetFields.length - 1;
        }
      }

      if (targetRowIndex < 0) return;

      const targetFieldsList = targetRowIndex === rowIndex
        ? fields
        : this.getEditableFieldsForRow(this.records[targetRowIndex]?.id);
      const targetField = targetFieldsList[targetFieldIndex];

      const targetRowId = this.records[targetRowIndex]?.id;

      if (!targetRowId || !targetField) return;

      if (targetRowIndex !== rowIndex) {
        const finalized = await this.finalizeRowEdit(rowId);
        if (!finalized) return;
        const rowEditingEnabled = this.enableEditing(targetRowId, targetField);
        if (rowEditingEnabled) {
          this.currentlyFocusedField = targetRowId;
        }
        return;
      }

      this.$nextTick(() => {
        this.focusField(targetRowId, targetField);
      });
    },
    async focusNextField(rowId, field) {
      if (!this.isRowEditing(rowId)) return;
      const fieldCommitted = await this.commitAndCreateFieldValue(rowId, field);
      if (!fieldCommitted) return;

      const fields = this.getEditableFieldsForRow(rowId);
      const fieldIndex = fields.indexOf(field);
      const rowIndex = this.records.findIndex((record) => String(record.id) === String(rowId));
      if (fieldIndex === -1 || rowIndex === -1) return;

      let targetRowIndex = rowIndex;
      let targetFieldIndex = fieldIndex + 1;

      if (targetFieldIndex >= fields.length) {
        targetRowIndex = rowIndex + 1;
        targetFieldIndex = 0;
      }

      if (targetRowIndex >= this.records.length) return;

      const targetFieldsList = targetRowIndex === rowIndex
        ? fields
        : this.getEditableFieldsForRow(this.records[targetRowIndex]?.id);
      const targetField = targetFieldsList[targetFieldIndex];

      const targetRowId = this.records[targetRowIndex]?.id;

      if (!targetRowId || !targetField) return;

      if (targetRowIndex !== rowIndex) {
        const finalized = await this.finalizeRowEdit(rowId);
        if (!finalized) return;
        const rowEditingEnabled = this.enableEditing(targetRowId, targetField);
        if (rowEditingEnabled) {
          this.currentlyFocusedField = targetRowId;
        }
        return;
      }

      this.$nextTick(() => {
        this.focusField(targetRowId, targetField);
      });
    },
    getInputComponent(rowId, field) {
      const ref = this.$refs[this.inputRefName(rowId, field)];
      return Array.isArray(ref) ? ref[0] : ref;
    },
    commitFieldValue(rowId, field) {
      const component = this.getInputComponent(rowId, field);
      if (component && typeof component.commitSearchValue === 'function') {
        component.commitSearchValue();
      }
    },
    commitRowFieldValues(rowId) {
      this.editableFields.forEach((field) => {
        this.commitFieldValue(rowId, field);
      });
    },
    async commitAndCreateFieldValue(rowId, field) {
      this.commitFieldValue(rowId, field);
      await this.$nextTick();

      const currentValue = this.getRawFieldValue(rowId, field);
      if (!this.shouldResolveDimensionValue(field, currentValue)) return true;

      const result = await this.resolveDimensionValue(rowId, field, currentValue);
      if (result?.error) {
        this.setRowRequestError(rowId, result.error);
        return false;
      }

      const createdValue = result?.value;
      if (
        this.normalizeComparisonValue(createdValue)
        !== this.normalizeComparisonValue(currentValue)
      ) {
        this.onFieldChange(rowId, field, createdValue);
        await this.$nextTick();
      }
      return true;
    },
    dimensionParentRequestKey(field, record) {
      const parentFields = {
        complementId: 'eventId',
        subGroupId: 'groupId',
        predictiveClassificationId: 'predictiveId',
      };
      const parentField = parentFields[field];
      return parentField ? String(record?.[parentField] ?? '') : '';
    },
    dimensionResolutionRequestPrefix(rowId, field) {
      return `${rowId}:${field}:`;
    },
    isDimensionFieldResolving(rowId, field) {
      const prefix = this.dimensionResolutionRequestPrefix(rowId, field);
      return Object.keys(this.dimensionResolutionRequests)
        .some((key) => key.startsWith(prefix));
    },
    resolveDimensionValue(rowId, field, value) {
      const record = this.mountRecord(rowId);
      const requestKey = [
        rowId,
        field,
        String(value ?? ''),
        this.dimensionParentRequestKey(field, record),
      ].join(':');
      if (this.dimensionResolutionRequests[requestKey]) {
        return this.dimensionResolutionRequests[requestKey];
      }

      const request = this.createProcedureDimension({ field, value, record })
        .finally(() => {
          this.$delete(this.dimensionResolutionRequests, requestKey);
        });
      this.$set(this.dimensionResolutionRequests, requestKey, request);
      return request;
    },
    focusField(rowId, field) {
      const component = this.getInputComponent(rowId, field);
      if (component && typeof component.focus === 'function') {
        component.focus();
      }
    },
    openSelectMenu(rowId, field) {
      const component = this.getInputComponent(rowId, field);
      if (component && typeof component.openMenu === 'function') {
        component.openMenu();
      }
    },
    async onObserveRow(item, options = {}) {
      const { focusField = 'carrierId', openSelect = false } = options;
      const isTableClick = Object.keys(options).length === 0;
      if (
        this.activeNewRecord
        && (!item || String(item.id) !== String(this.activeNewRecord.id))
      ) return;

      if (!item || !item.id) {
        if (this.currentlyFocusedField) {
          this.commitRowFieldValues(this.currentlyFocusedField);
          await this.$nextTick();
          const finalized = await this.finalizeRowEdit(this.currentlyFocusedField);
          if (!finalized) return;
        }
        return;
      }

      if (
        isTableClick
        && String(item.id) === String(this.currentlyFocusedField)
        && this.isRowEditing(item.id)
      ) {
        if (item.isNew) return;
        this.commitRowFieldValues(item.id);
        await this.$nextTick();
        await this.finalizeRowEdit(item.id);
        return;
      }

      if (this.currentlyFocusedField != null && String(item.id) !== String(this.currentlyFocusedField)) {
        this.commitRowFieldValues(this.currentlyFocusedField);
        await this.$nextTick();
        const finalized = await this.finalizeRowEdit(this.currentlyFocusedField);
        if (!finalized) return;
      }

      if (!this.isRowEditing(item.id)) {
        const rowEditingEnabled = this.enableEditing(item.id, focusField);
        if (!rowEditingEnabled) return;
        this.currentlyFocusedField = item.id;
        if (openSelect) {
          this.$nextTick(() => {
            this.openSelectMenu(item.id, focusField);
          });
        }
      }
    },
    getFieldValue(rowId, field) {
      const formRow = this.formRows[rowId];
      if (formRow && Object.prototype.hasOwnProperty.call(formRow, field)) {
        return formRow[field];
      }
      const record = this.findRecord(rowId);
      return record ? record[field] : null;
    },
    getRawFieldValue(rowId, field) {
      if (this.formRows[rowId] && Object.prototype.hasOwnProperty.call(this.formRows[rowId], field)) {
        return this.formRows[rowId][field];
      }
      const record = this.findRecord(rowId);
      return record ? record[field] : null;
    },
    getFieldOptions(field) {
      const optionsByField = {
        carrierId: this.filteredCarrierOptions,
        carrierIds: this.filteredCarrierOptions,
        procedureCodeId: this.procedureCodeOptions,
        procedureDescriptionId: this.procedureDescriptionOptions,
        tussCodeId: this.tussCodeOptions,
        eventId: this.eventOptions,
        groupId: this.groupOptions,
        subGroupId: this.subGroupOptions,
        complementId: this.complementOptions,
        predictiveId: this.predictiveOptions,
        predictiveClassificationId: this.predictiveClassificationOptions,
      };
      return optionsByField[field] || [];
    },
    getEditingOptions(rowId, field) {
      const options = this.getFieldOptions(field);
      const value = this.getRawFieldValue(rowId, field);
      const values = Array.isArray(value)
        ? value
        : (value === null || value === undefined || value === '' ? [] : [value]);
      if (values.length === 0) return options;

      const missing = values
        .filter((id) => !options.some((option) => String(option.id) === String(id)))
        .map((id) => ({ id, name: String(id) }));
      return [...missing, ...options];
    },
    getDependentEditingOptions(rowId, field) {
      const value = this.getRawFieldValue(rowId, field);
      if (value === null || value === undefined || value === '') return [];
      const option = this.getFieldOptions(field)
        .find((item) => String(item.id) === String(value));
      return [option || { id: value, name: String(value) }];
    },
    hasExistingDimensionValue(rowId, field) {
      return this.getExistingDimensionIds(rowId, field).length > 0;
    },
    dimensionInputKey(rowId, field) {
      return `${rowId}-${field}-${this.getRawFieldValue(rowId, field) || 'none'}`;
    },
    dependentInputKey(rowId, field, parentField) {
      return [
        rowId,
        field,
        this.getRawFieldValue(rowId, parentField) || 'none',
        this.getRawFieldValue(rowId, field) || 'none',
      ].join('-');
    },
    getCarrierItems(rowId) {
      const value = this.getRawFieldValue(rowId, 'carrierIds');
      const ids = Array.isArray(value) && value.length
        ? value
        : [this.getRawFieldValue(rowId, 'carrierId')].filter(
          (id) => id !== null && id !== undefined && id !== '',
        );
      return ids.map((id) => this.resolveCarrierItem(id));
    },
    resolveCarrierItem(id) {
      const option = this.filteredCarrierOptions
        .find((item) => String(item.id) === String(id));
      return option ? { id: option.id, name: option.name } : { id, name: String(id) };
    },
    getFieldDisplayValue(rowId, field) {
      const value = this.getRawFieldValue(rowId, field);
      if (value === null || value === undefined || value === '') return '';
      const option = this.getFieldOptions(field)
        .find((item) => String(item.id) === String(value));
      return option ? option.name : String(value);
    },
    isCreatableDimensionField(field) {
      return [
        'eventId',
        'complementId',
        'groupId',
        'subGroupId',
        'predictiveId',
        'predictiveClassificationId',
      ].includes(field);
    },
    hasFieldOptionId(field, value) {
      return this.getFieldOptions(field)
        .some((option) => String(option.id) === String(value));
    },
    shouldResolveDimensionValue(field, value) {
      return this.isCreatableDimensionField(field)
        && this.hasFieldValue(value)
        && typeof value === 'string'
        && !this.hasFieldOptionId(field, value);
    },
    async resolveCreatedDimensionFromInput(rowId, field, originalValue) {
      if (!this.shouldResolveDimensionValue(field, originalValue)) return;

      const result = await this.resolveDimensionValue(rowId, field, originalValue);
      if (result?.error) {
        this.setRowRequestError(rowId, result.error);
        return;
      }

      if (
        this.normalizeComparisonValue(this.getRawFieldValue(rowId, field))
        !== this.normalizeComparisonValue(originalValue)
      ) return;

      const createdValue = result?.value;
      if (
        this.normalizeComparisonValue(createdValue)
        !== this.normalizeComparisonValue(originalValue)
      ) {
        this.onFieldChange(rowId, field, createdValue);
      }
    },
    async resolveRowDimensionValues(rowId) {
      this.commitRowFieldValues(rowId);
      await this.$nextTick();

      const dimensionFields = [
        'eventId',
        'complementId',
        'groupId',
        'subGroupId',
        'predictiveId',
        'predictiveClassificationId',
      ];

      return dimensionFields.reduce(async (resolved, field) => {
        const canResolve = await resolved;
        if (!canResolve) return false;

        const currentValue = this.getRawFieldValue(rowId, field);
        if (!this.shouldResolveDimensionValue(field, currentValue)) return true;

        const result = await this.resolveDimensionValue(rowId, field, currentValue);
        if (result?.error) {
          this.setRowRequestError(rowId, result.error);
          return false;
        }

        const createdValue = result?.value;
        if (
          this.normalizeComparisonValue(createdValue)
          !== this.normalizeComparisonValue(currentValue)
        ) {
          this.onFieldChange(rowId, field, createdValue);
          await this.$nextTick();
        }
        return true;
      }, Promise.resolve(true));
    },
    inputRefName(rowId, field) {
      return `procedure-report-field-${rowId}-${field}`;
    },
    isFieldUpdated(rowId, field) {
      return Boolean(this.updatedFields[rowId] && this.updatedFields[rowId][field]);
    },
    hasRowUpdates(rowId) {
      const updated = this.updatedFields[rowId];
      return Boolean(updated && Object.keys(updated).length > 0);
    },
    rowHasChanges(rowId) {
      if (this.hasRowUpdates(rowId)) return true;
      const record = this.findRecord(rowId);
      const formRow = this.formRows[rowId];
      if (!record || !formRow) return false;
      return this.editableFields.some((field) => (
        this.normalizeComparisonValue(formRow[field]) !== this.normalizeComparisonValue(record[field])
      ));
    },
    isRowEditing(rowId) {
      return Boolean(this.editingRows[rowId]);
    },
    markFieldAsUpdated(rowId, field, isUpdate) {
      if (!this.updatedFields[rowId]) {
        this.$set(this.updatedFields, rowId, {});
      }
      if (isUpdate) {
        this.$set(this.updatedFields[rowId], field, true);
      } else if (this.updatedFields[rowId][field]) {
        this.$delete(this.updatedFields[rowId], field);
      }
    },
    normalizeComparisonValue(value) {
      if (Array.isArray(value)) return value.length ? value.map(String).join(',') : '';
      if (value === null || value === undefined || value === '') return '';
      return String(value);
    },
    loadMoreRecords() {
      if (!this.hasMore || this.loadingTable) return;
      this.updateTableOptions({
        page: this.currentPage + 1,
        itemsPerPage: this.itemsPerPage,
        sortBy: this.sortBy ? [this.sortBy] : [],
        sortDesc: this.sortBy ? [this.sortDesc] : [],
      });
    },
    onFieldChange(rowId, field, value) {
      if (!this.isRowEditing(rowId)) return;
      this.ensureFormRow(rowId);
      if (this.normalizeComparisonValue(this.formRows[rowId][field])
        === this.normalizeComparisonValue(value)) return;
      this.$set(this.formRows[rowId], field, value);
      this.updateFieldState(rowId, field, value);
      const dependentFields = {
        eventId: 'complementId',
        groupId: 'subGroupId',
        predictiveId: 'predictiveClassificationId',
      };
      const dependentField = dependentFields[field];
      if (dependentField) {
        this.$set(this.formRows[rowId], dependentField, null);
        this.updateFieldState(rowId, dependentField, null);
      }
      this.resolveCreatedDimensionFromInput(rowId, field, value);
      if (this.findRecord(rowId)?.isNew && this.getMissingRequiredFields(rowId).length === 0) {
        this.removeRowIdWithError(rowId);
      }
    },
    syncFormRows(records) {
      const nextFormRows = {};
      const nextUpdatedFields = {};
      const nextEditingRows = {};
      records.forEach((record) => {
        const existingRow = this.formRows[record.id] || {};
        const updated = this.updatedFields[record.id] || {};
        const hasUpdates = Object.keys(updated).length > 0;
        nextFormRows[record.id] = hasUpdates ? { ...record, ...existingRow } : { ...record };
        nextUpdatedFields[record.id] = updated;
        nextEditingRows[record.id] = Boolean(this.editingRows[record.id]);
      });
      this.formRows = nextFormRows;
      this.updatedFields = nextUpdatedFields;
      this.editingRows = nextEditingRows;
    },
    mountRecord(rowId) {
      return {
        ...(this.findRecord(rowId) || {}),
        ...(this.formRows[rowId] || {}),
      };
    },
    async sendRecord(rowId) {
      if (this.isRowSaving(rowId)) return false;

      const payload = this.mountRecord(rowId);
      this.$set(this.savingRows, rowId, true);
      const result = await this.saveProcedureRecord(payload);

      if (Array.isArray(result?.created)) {
        return this.handleCreateResult(rowId, result);
      }

      const recordStatus = result?.status ?? result?.response?.status ?? result;

      if ([200, 201, 204].includes(recordStatus)) {
        const savedRowId = result?.data?.id ?? rowId;
        this.rowIdsUpdated = [...new Set([...this.rowIdsUpdated, savedRowId])];
        this.removeRowIdWithError(rowId);
        this.showProcedureReportSnackbar({
          type: 'success',
          message: payload.isNew
            ? String(this.$t('procedureReport.messages.createSuccess'))
            : String(this.$t('procedureReport.messages.saveSuccess')),
          show: true,
        });
        setTimeout(() => {
          this.rowIdsUpdated = this.rowIdsUpdated.filter((id) => id !== savedRowId);
        }, 5000);
        this.$delete(this.savingRows, rowId);
        return savedRowId;
      }

      this.setRowRequestError(rowId, result);
      this.$delete(this.savingRows, rowId);
      return false;
    },
    handleCreateResult(rowId, result) {
      this.$delete(this.savingRows, rowId);

      if (!result.ok || !result.createdIds?.length) {
        this.addRowIdWithError(rowId);
        this.setRowErrorInfo(rowId, { message: result.feedback?.message || '', items: [] });
        return false;
      }

      this.$delete(this.formRows, rowId);
      this.$delete(this.updatedFields, rowId);
      this.$delete(this.editingRows, rowId);
      this.$delete(this.rowErrors, rowId);
      this.rowIdWithError = this.rowIdWithError.filter((id) => String(id) !== String(rowId));
      this.selectedProcedureReports = this.selectedProcedureReports
        .filter((record) => String(record.id) !== String(rowId));

      const { createdIds } = result;
      this.rowIdsUpdated = [...new Set([...this.rowIdsUpdated, ...createdIds])];
      this.showProcedureReportSnackbar({
        type: result.feedback?.type ?? 'success',
        message: result.feedback?.message || String(this.$t('procedureReport.messages.createSuccess')),
        show: true,
      });
      setTimeout(() => {
        this.rowIdsUpdated = this.rowIdsUpdated.filter((id) => !createdIds.includes(id));
      }, 5000);
      return createdIds[0];
    },
    addNewProcedure() {
      const existingNewRecord = this.records.find((record) => record.isNew);
      if (existingNewRecord) {
        this.onObserveRow(existingNewRecord, { focusField: 'carrierId', openSelect: true });
        return;
      }

      this.addProcedureRecord();
      this.$nextTick(() => {
        this.scrollTableToTop();
        const newRecord = this.records[0];
        if (newRecord?.isNew) {
          this.onObserveRow(newRecord, { focusField: 'carrierId', openSelect: true });
        }
      });
    },
    async discardNewProcedure(rowId) {
      if (this.isRowSaving(rowId)) return;

      const discarded = await this.discardProcedureRecord(rowId);
      if (!discarded) return;

      if (String(this.currentlyFocusedField) === String(rowId)) {
        this.currentlyFocusedField = null;
      }
      this.$delete(this.formRows, rowId);
      this.$delete(this.updatedFields, rowId);
      this.$delete(this.editingRows, rowId);
      this.$delete(this.savingRows, rowId);
      this.$delete(this.rowErrors, rowId);
      Object.keys(this.dimensionResolutionRequests)
        .filter((key) => key.startsWith(`${rowId}:`))
        .forEach((key) => this.$delete(this.dimensionResolutionRequests, key));
      this.rowIdWithError = this.rowIdWithError.filter((id) => String(id) !== String(rowId));
      this.selectedProcedureReports = this.selectedProcedureReports
        .filter((record) => String(record.id) !== String(rowId));
    },
    scrollTableToTop() {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    getStatusIcon(status) {
      return (getStatusConfig(status) ?? getDefaultStatusConfig()).icon;
    },
    getStatusColor(status) {
      return (getStatusConfig(status) ?? getDefaultStatusConfig()).color;
    },
    getStatusLabel(status) {
      const config = getStatusConfig(status) ?? getDefaultStatusConfig();
      return this.$t(config.labelKey);
    },
    shouldShowRowActionsMenu(item) {
      return !item.isNew && !this.hasRowUpdates(item.id) && !this.rowError(item);
    },
    async approveProcedure(item) {
      await this.approveProcedures([item.id]);
    },
    openJustification(item) {
      this.openJustificationModal(item);
    },
    addRowIdWithError(rowId) {
      if (!this.rowIdWithError.includes(rowId)) {
        this.rowIdWithError = [...this.rowIdWithError, rowId];
      }
    },
    removeRowIdWithError(rowId) {
      if (this.rowIdWithError.includes(rowId)) {
        this.rowIdWithError = this.rowIdWithError.filter((id) => id !== rowId);
      }
      this.clearRowErrorInfo(rowId);
    },
    setRowClass(item) {
      if (this.activeNewRecord && String(item.id) !== String(this.activeNewRecord.id)) {
        return 'procedure-report-row--blocked';
      }

      if (this.rowIdsUpdated.includes(item.id)) {
        return 'success';
      }

      if (this.rowIdWithError.includes(item.id)) {
        return 'error foreground-accent--text';
      }

      if (item.isNew) {
        return 'procedure-report-row--new';
      }

      return '';
    },
    async saveRecord(rowId) {
      if (!this.validateNewRecord(rowId)) return false;

      const dimensionsResolved = await this.resolveRowDimensionValues(rowId);
      if (!dimensionsResolved) return false;

      this.releaseRowEditing(rowId);
      const savedRowId = await this.sendRecord(rowId);
      if (savedRowId === false) return false;

      this.clearUpdatedFields(rowId);
      if (savedRowId !== rowId) {
        this.clearUpdatedFields(savedRowId);
        this.disableEditing(savedRowId);
      }
      return true;
    },
    updateFieldState(rowId, field, value) {
      const record = this.findRecord(rowId);
      const original = record ? record[field] : '';
      const isUpdate = this.normalizeComparisonValue(value) !== this.normalizeComparisonValue(original);
      this.markFieldAsUpdated(rowId, field, isUpdate);
    },
    isDialogEditableField(field) {
      return this.dialogEditableFields.includes(field);
    },
    isFieldValueEmpty(value) {
      if (value === null || value === undefined) return true;
      return typeof value !== 'string' || value.trim().length === 0;
    },
    consumeSkipDialogFieldFocus(field) {
      if (!field || !this.skipDialogFieldFocus[field]) return false;
      this.$set(this.skipDialogFieldFocus, field, false);
      return true;
    },
    setSkipDialogFieldFocus(field, shouldSkip) {
      if (!field) return;
      this.$set(this.skipDialogFieldFocus, field, shouldSkip);
    },
    async onDialogFieldClick(item, field) {
      if (!this.isDialogEditableField(field)) return;
      if (!this.isRowEditing(item.id)) {
        await this.onObserveRow(item, { focusField: field });
      }
      this.openFieldDialog(item, field);
    },
    async onDialogFieldFocus(item, field) {
      if (!this.isDialogEditableField(field)) {
        await this.onObserveRow(item);
        return;
      }

      if (this.consumeSkipDialogFieldFocus(field)) {
        return;
      }

      if (!this.isRowEditing(item.id)) {
        await this.onObserveRow(item, { focusField: field });
      }

      const fieldValue = this.getRawFieldValue(item.id, field);
      if (this.isFieldValueEmpty(fieldValue)) {
        this.openFieldDialog(item, field);
      }
    },
    openFieldDialog(item, field) {
      if (!item || !item.id || !field || !this.isDialogEditableField(field)) return;
      if (!this.isRowEditing(item.id)) return;
      if (
        this.fieldDialog.visible
        && this.fieldDialog.rowId === item.id
        && this.fieldDialog.field === field
      ) {
        return;
      }

      this.fieldDialog.rowId = item.id;
      this.fieldDialog.field = field;
      this.fieldDialog.value = this.getFieldDisplayValue(item.id, field);
      this.fieldDialog.visible = true;
    },
    closeFieldDialog(restoreFocus = true) {
      const { rowId, field } = this.fieldDialog;

      this.fieldDialog.visible = false;
      this.fieldDialog.rowId = null;
      this.fieldDialog.field = null;
      this.fieldDialog.value = '';

      if (restoreFocus && rowId && field) {
        this.setSkipDialogFieldFocus(field, true);
        this.$nextTick(() => {
          this.focusField(rowId, field);
        });
        return;
      }

      if (field) {
        this.setSkipDialogFieldFocus(field, false);
      }
    },
    cancelFieldDialog() {
      this.closeFieldDialog(true);
    },
    applyDialogFieldSave(rowId, field, value) {
      this.onFieldChange(rowId, field, value);
    },
    saveFieldDialog(value) {
      const { rowId, field } = this.fieldDialog;
      if (rowId && field) {
        this.applyDialogFieldSave(rowId, field, value);
      }
      this.closeFieldDialog(true);
    },
  },
  created() {
    this.fetchCarrierOptions({ page: 0, size: 20 });
  },
};
</script>

<style lang="scss">
@import "@/shared/styles/procedureReport/table.scss";

.procedure-report-field--loading {
  .v-input__append-inner {
    align-items: center;
    display: flex;
    min-width: 20px;
  }

  .v-input__append-inner::after {
    animation: procedure-report-field-spin 0.8s linear infinite;
    border: 2px solid var(--v-primary-base);
    border-radius: 50%;
    border-top-color: transparent;
    content: "";
    display: inline-block;
    height: 16px;
    width: 16px;
  }

  .v-input__append-inner .v-input__icon {
    display: none;
  }
}

@keyframes procedure-report-field-spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
